import { ToolItem } from '../types';

export interface SecurityToolItem extends ToolItem {
  securityCategory: 'passwords' | 'hashes' | 'encoding-jwt' | 'tokens-ids';
  securityCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const SECURITY_CATEGORIES_TABS = [
  { id: 'all', label: 'All Security Tools', icon: 'ShieldCheck' },
  { id: 'passwords', label: 'Passwords & Passphrases', icon: 'KeyRound' },
  { id: 'hashes', label: 'Cryptographic Hashes', icon: 'Lock' },
  { id: 'encoding-jwt', label: 'JWT & Encoding', icon: 'FileCode' },
  { id: 'tokens-ids', label: 'Tokens & Identifiers', icon: 'Sparkles' }
];

export const SECURITY_TOOLS_DATA: SecurityToolItem[] = [
  // --- PASSWORDS & PASSPHRASES (4 Tools) ---
  {
    id: 'password-generator',
    name: 'Password Generator',
    description: 'Generate customizable, cryptographically strong passwords with uppercase, numbers, and symbols.',
    securityCategory: 'passwords',
    securityCategoryLabel: 'Passwords & Passphrases',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'KeyRound',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'passphrase-generator',
    name: 'Passphrase Generator',
    description: 'Generate memorable multi-word passphrases (Diceware style) for high-security master passwords.',
    securityCategory: 'passwords',
    securityCategoryLabel: 'Passwords & Passphrases',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'FileText',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'password-strength-checker',
    name: 'Password Strength Checker',
    description: 'Analyze password entropy, crack time estimations, length, and character diversity.',
    securityCategory: 'passwords',
    securityCategoryLabel: 'Passwords & Passphrases',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'ShieldAlert',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'entropy-calculator',
    name: 'Entropy Calculator',
    description: 'Calculate exact mathematical information entropy bits (bits of randomness) for any secret.',
    securityCategory: 'passwords',
    securityCategoryLabel: 'Passwords & Passphrases',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Calculator',
    actionType: 'generic'
  },

  // --- CRYPTOGRAPHIC HASHES (7 Tools) ---
  {
    id: 'md5',
    name: 'MD5',
    description: 'Compute 128-bit MD5 checksum hashes for strings or data verification.',
    securityCategory: 'hashes',
    securityCategoryLabel: 'Cryptographic Hashes',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'sha1',
    name: 'SHA1',
    description: 'Calculate 160-bit SHA-1 hash strings for data integrity checking.',
    securityCategory: 'hashes',
    securityCategoryLabel: 'Cryptographic Hashes',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Lock',
    actionType: 'generic'
  },
  {
    id: 'sha256',
    name: 'SHA256',
    description: 'Generate secure 256-bit SHA-2 hashes for authentication and digital signatures.',
    securityCategory: 'hashes',
    securityCategoryLabel: 'Cryptographic Hashes',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'ShieldCheck',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'sha512',
    name: 'SHA512',
    description: 'Compute high-security 512-bit SHA-2 cryptographic hash digests.',
    securityCategory: 'hashes',
    securityCategoryLabel: 'Cryptographic Hashes',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Cpu',
    actionType: 'generic'
  },
  {
    id: 'hmac',
    name: 'HMAC Generator',
    description: 'Compute Keyed-Hash Message Authentication Codes using secret key algorithms.',
    securityCategory: 'hashes',
    securityCategoryLabel: 'Cryptographic Hashes',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Key',
    actionType: 'generic'
  },
  {
    id: 'bcrypt-generator',
    name: 'Bcrypt Generator',
    description: 'Generate salted Bcrypt password hashes with configurable work factor cost rounds.',
    securityCategory: 'hashes',
    securityCategoryLabel: 'Cryptographic Hashes',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'Lock',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'hash-compare',
    name: 'Hash Compare',
    description: 'Compare two cryptographic hashes side-by-side with constant-time string verification.',
    securityCategory: 'hashes',
    securityCategoryLabel: 'Cryptographic Hashes',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'GitCompare',
    actionType: 'generic'
  },

  // --- JWT & ENCODING (5 Tools) ---
  {
    id: 'jwt-decoder',
    name: 'JWT Decoder',
    description: 'Decode JSON Web Token headers, payload claims, and signature algorithms safely in browser.',
    securityCategory: 'encoding-jwt',
    securityCategoryLabel: 'JWT & Encoding',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Key',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'jwt-viewer',
    name: 'JWT Viewer',
    description: 'Inspect token expiration times (exp), issuer details, and custom authentication claims.',
    securityCategory: 'encoding-jwt',
    securityCategoryLabel: 'JWT & Encoding',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Eye',
    actionType: 'generic'
  },
  {
    id: 'base64',
    name: 'Base64',
    description: 'Encode and decode UTF-8 text or binary files into Base64 format.',
    securityCategory: 'encoding-jwt',
    securityCategoryLabel: 'JWT & Encoding',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Binary',
    actionType: 'generic'
  },
  {
    id: 'url-encode',
    name: 'URL Encode',
    description: 'Percent-encode special URI query characters or decode encoded URL strings.',
    securityCategory: 'encoding-jwt',
    securityCategoryLabel: 'JWT & Encoding',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Globe',
    actionType: 'generic'
  },
  {
    id: 'html-encode',
    name: 'HTML Encode',
    description: 'Escape special HTML entities like &lt;, &gt;, and &amp; to prevent XSS vulnerability.',
    securityCategory: 'encoding-jwt',
    securityCategoryLabel: 'JWT & Encoding',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'Code',
    actionType: 'generic'
  },

  // --- TOKENS & IDENTIFIERS (4 Tools) ---
  {
    id: 'random-token-generator',
    name: 'Random Token Generator',
    description: 'Generate high-entropy random API keys, secret bearer tokens, and session credentials.',
    securityCategory: 'tokens-ids',
    securityCategoryLabel: 'Tokens & Identifiers',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Sparkles',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'uuid-generator',
    name: 'UUID Generator',
    description: 'Generate bulk cryptographically secure v4 UUIDs (Universally Unique Identifiers).',
    securityCategory: 'tokens-ids',
    securityCategoryLabel: 'Tokens & Identifiers',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Dices',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'nanoid-generator',
    name: 'Nano ID Generator',
    description: 'Generate compact, URL-friendly, unique string IDs with custom alphabet parameters.',
    securityCategory: 'tokens-ids',
    securityCategoryLabel: 'Tokens & Identifiers',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Terminal',
    actionType: 'generic'
  },
  {
    id: 'secure-random-generator',
    name: 'Secure Random Generator',
    description: 'Generate cryptographically uniform pseudo-random bytes and numbers using Web Crypto API.',
    securityCategory: 'tokens-ids',
    securityCategoryLabel: 'Tokens & Identifiers',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Shield',
    actionType: 'generic'
  }
];
